import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { ReportService } from '@ndk/ng-reports';
import { Report, ReportCreateCommand, ReportDeleteCommand, ReportUpdateCommand } from '@ndk/ng-reports/component/shared/model/ndd-ng-reports.model';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ICoreConfigInternal } from '@core/core.config.model';

import { ICreateReportCreatedAuditCommand, ICreateReportDeletedAuditCommand, ICreateReportUpdatedAuditCommand } from './report-new.model';

@Injectable({
    providedIn: 'root',
})
export class ReportNewService extends ReportService {
    private apiUrl: string;

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        public http: HttpClient,
    ) {
        super(config, http);
        this.apiUrl = `${ config.apiEndpoint }api/new-reports`;
    }

    public isAllowed(): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/is-allowed`);
    }

    // Sobrescreve a chamada de criação do relatório para criar a auditoria do portal
    public create(reportCreateCommand: ReportCreateCommand): Observable<number> {
        return super.create(reportCreateCommand)
            .pipe(
                mergeMap((response: number) => {
                    if (response) {
                        return this.createReportCreatedAudit({ reportName: reportCreateCommand.name })
                            .pipe(map(() => response));
                    }

                    return of(response);
                }),
            );
    }

    // Sobrescreve a chamada de edição do relatório para criar a auditoria do portal
    public update(reportUpdateCommand: ReportUpdateCommand): Observable<boolean> {
        return super.update(reportUpdateCommand)
            .pipe(
                mergeMap((response: boolean) => {
                    if (response) {
                        return this.createReportUpdatedAudit({ reportName: reportUpdateCommand.name })
                            .pipe(map(() => response));
                    }

                    return of(response);
                }),
            );
    }

    // Sobrescreve a chamada de apagar os relatórios para criar a auditoria do portal
    public delete(reportCommand: ReportDeleteCommand): Observable<boolean> {
        return forkJoin(reportCommand.reportIds.map((id: string) => super.get(id)))
            .pipe(
                mergeMap((reports: Report[]) => {
                    return super.delete({ reportIds: reports.map((report: Report) => report.id) })
                        .pipe(
                            mergeMap((response: boolean) => {
                                if (response) {
                                    return this.createReportDeletedAudit({ reportsName: reports.map((report: Report) => report.name) })
                                        .pipe(map(() => response));
                                }

                                return of(response);
                            }));
                }),
            );
    }

    private createReportCreatedAudit(command: ICreateReportCreatedAuditCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/create-report-created-audit`, command);
    }

    private createReportUpdatedAudit(command: ICreateReportUpdatedAuditCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/create-report-updated-audit`, command);
    }

    private createReportDeletedAudit(command: ICreateReportDeletedAuditCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/create-report-deleted-audit`, command);
    }
}
